import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { isProductionDomain, isAdminDomain } from '../../utils/domain';
import './ConstructionGate.css';

const CONE_SCRIPT_URL = 'https://storage.googleapis.com/usub-platform-images/config/cone.js';
const LEGACY_SESSION_KEY = 'usub_cone_proceed';

interface ConstructionGateProps {
    children: React.ReactNode;
}

declare global {
    interface Window {
        __USUB_CONE__?: { on?: boolean };
        __USUB_CONE_DISMISS_PATH__?: string;
    }
}

const loadConeFromScript = (): Promise<boolean | null> => {
    return new Promise((resolve) => {
        const existing = document.querySelector<HTMLScriptElement>('script[data-usub-cone="1"]');
        if (existing) {
            existing.remove();
        }

        delete window.__USUB_CONE__;

        const script = document.createElement('script');
        script.dataset.usubCone = '1';
        script.async = true;
        script.src = `${CONE_SCRIPT_URL}?t=${Date.now()}`;
        script.onload = () => {
            const on = window.__USUB_CONE__?.on === true;
            resolve(on);
        };
        script.onerror = () => resolve(null);
        document.head.appendChild(script);
    });
};

const loadConeFromLocalJson = async (): Promise<boolean | null> => {
    try {
        const res = await fetch(`/cone.json?t=${Date.now()}`, { cache: 'no-store' });
        if (!res.ok) return null;
        const data = await res.json();
        return data?.on === true;
    } catch {
        return null;
    }
};

const ConstructionGate: React.FC<ConstructionGateProps> = ({ children }) => {
    const location = useLocation();
    const currentKey = `${location.pathname}${location.search}`;
    const [checking, setChecking] = useState(true);
    const [coneOn, setConeOn] = useState(false);
    // Proceed only dismisses for the current path; any navigation/reload re-shows the gate
    const [dismissedPath, setDismissedPath] = useState<string | null>(() => {
        const fromHtml = typeof window !== 'undefined' ? window.__USUB_CONE_DISMISS_PATH__ : undefined;
        return fromHtml || null;
    });

    useEffect(() => {
        // Clear legacy "proceed forever this session" flag
        try {
            sessionStorage.removeItem(LEGACY_SESSION_KEY);
        } catch {
            // ignore
        }

        let cancelled = false;

        const checkCone = async () => {
            if (!isProductionDomain() || isAdminDomain()) {
                if (!cancelled) {
                    setConeOn(false);
                    setChecking(false);
                }
                return;
            }

            const fromScript = await loadConeFromScript();
            const enabled = fromScript !== null ? fromScript : await loadConeFromLocalJson();

            if (!cancelled) {
                setConeOn(enabled === true);
                setChecking(false);
                // Honor HTML "Proceed anyway" for this first paint only
                if (window.__USUB_CONE_DISMISS_PATH__) {
                    setDismissedPath(window.__USUB_CONE_DISMISS_PATH__);
                    delete window.__USUB_CONE_DISMISS_PATH__;
                }
            }
        };

        checkCone();
        return () => {
            cancelled = true;
        };
    }, []);

    // Every route change re-blocks while cone is on
    useEffect(() => {
        setDismissedPath((prev) => (prev === currentKey ? prev : null));
    }, [currentKey]);

    const handleProceed = () => {
        setDismissedPath(currentKey);
        const htmlGate = document.getElementById('usub-cone-gate');
        if (htmlGate) htmlGate.remove();
        document.documentElement.classList.remove('usub-cone-lock');
        document.body.classList.remove('usub-cone-lock');
    };

    const blocked = coneOn && dismissedPath !== currentKey;

    if (checking) {
        return <div className="construction-gate-loading" aria-hidden="true" />;
    }

    if (!blocked) {
        return <>{children}</>;
    }

    return (
        <div className="construction-gate">
            <div className="construction-gate-main">
                <svg
                    className="construction-gate-icon"
                    viewBox="0 0 64 64"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                >
                    <path
                        d="M32 6L54 54H10L32 6Z"
                        stroke="currentColor"
                        strokeWidth="3"
                        strokeLinejoin="round"
                    />
                    <path d="M18 40H46" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                    <path d="M22 28H42" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                    <path d="M8 54H56" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                </svg>
                <h1 className="construction-gate-title">Website under construction</h1>
                <p className="construction-gate-message">
                    Sorry for the inconvenience. We&apos;ll be back shortly.
                </p>
            </div>
            <button
                type="button"
                className="construction-gate-proceed"
                onClick={handleProceed}
            >
                Proceed anyway
            </button>
        </div>
    );
};

export default ConstructionGate;
